import React, { useEffect, useState, ChangeEvent } from 'react';
import Box from '@mui/material/Box';
import Pagination from '@mui/material/Pagination';
import { handleError } from '../../shared/ErrorCatching';
import FavoriteImageModal from './FavoriteImageModal';
import { withRouter, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { getAllFavoriteImages } from './queries/getFavoriteImages';
import { styled } from '@mui/material/styles';
import PhotographerSelectFilter from './PhotographerSelectFilter';
import { Translate } from '../../shared/LanguageService';

let PhotoPagination = styled(Pagination)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    flex: '1 auto',
    '& .MuiPaginationItem-root': {
        fontSize: '20px'
    },
    '& .MuiPaginationItem-icon': {
        fontSize: '20px'
    }
}));

const NUMBER_OF_IMAGES_PER_PAGE = 100;

type FavoriteImage = {
    id: number;
    reference: number;
    photographerName: string;
};

export type Photographer = {
    name: string;
    id: number;
};

type FavoriteImagesPageProps = {
    numberOfPages: number;
    availablePhotographers: Photographer[];
    onChangePage: (event, value) => void;
    onChangePhotographerFilter: (photographer: Photographer | null) => void;
    selectedPhotographer: Photographer | null;
    previousSelectedPhotographer: Photographer | null;
    currentPage: number;
    list: FavoriteImage[];
    match: any;
};

const FavoriteImagesPage = ({
    availablePhotographers,
    numberOfPages,
    onChangePage,
    onChangePhotographerFilter,
    selectedPhotographer,
    previousSelectedPhotographer,
    currentPage,
    list,
    match
}: FavoriteImagesPageProps) => {
    const [showModal, setShowModal] = useState<boolean>(false);

    const getSelectedImageId = (): number | null => {
        let { selectedImageId } = match.params;
        return selectedImageId !== undefined ? Number(selectedImageId) : null;
    };

    const handlePageChangeClick = (event, value) => {
        onChangePage(event, value);
    };

    let images = list
        ? list.map((x) => {
              if (!x.reference !== undefined)
                  return {
                      id: x.id,
                      original: `/api/file/${x.reference}`,
                      thumbnail: `/api/file/${x.reference}`,
                      photographerName: x.photographerName,
                      reference: x.reference
                  };
          })
        : [];

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const handleSelectedPhotographer = (photographer: Photographer) => {
        onChangePhotographerFilter(photographer);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: '2rem' }}>
                {numberOfPages > 1 ? (
                    <PhotoPagination
                        sx={{ paddingTop: '19px' }}
                        count={numberOfPages}
                        onChange={handlePageChangeClick}
                        page={currentPage}
                    />
                ) : null}
                <PhotographerSelectFilter
                    handleSelectedPhotographer={handleSelectedPhotographer}
                    selectedPhotographer={selectedPhotographer}
                    previousSelectedPhotographer={previousSelectedPhotographer}
                    availablePhotographers={availablePhotographers}
                    defaultOptionLabel={Translate(
                        'favorite-images/photographer-filter-default-option'
                    )}
                    filterTitleLabel={Translate('favorite-images/photographer-filter-title')}
                />
            </Box>
            <Box sx={{ display: 'flex ' }}>
                <ul className={'favourite-images-list'}>
                    {images.map((image) => {
                        return (
                            <li
                                key={image.reference}
                                className={'favourite-image-list-item'}
                                data-id={image.id}
                                onClick={() => setShowModal(true)}
                            >
                                <Link to={`/favorite-images/${image.id}`}>
                                    <div className={'favorite-image-overlay'} />
                                    <img
                                        alt="An image marked as favorite by our internal personal."
                                        className={'favourite-image-list-item-image'}
                                        src={image.thumbnail}
                                    />
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            </Box>
            {showModal ? (
                <FavoriteImageModal
                    onClose={handleCloseModal}
                    images={list}
                    showModal={showModal}
                    selectedImageId={getSelectedImageId()}
                />
            ) : null}
        </Box>
    );
};

function DataLayer(props) {
    let [currentPage, setCurrentPage] = useState<number>(1);
    let [selectedPhotographer, setSelectedPhotographer] = useState<Photographer | null>(null);

    let { isError, error, data } = useQuery(
        ['favorite-images', { currentPage, selectedPhotographerId: selectedPhotographer }],
        () =>
            getAllFavoriteImages(
                currentPage,
                selectedPhotographer ? selectedPhotographer.id : undefined
            ),
        {
            keepPreviousData: true
        }
    );

    useEffect(() => {
        /**
         * When we change photographer filter we need to reset current page
         * in order to not risk ending up with a blank page, due to not enough
         * images as previous photographer(/all photographers) had(/have).
         */
        setCurrentPage(1);
    }, [selectedPhotographer]);

    function handlePageChange(event: ChangeEvent<unknown>, value: number) {
        setCurrentPage(value);
    }

    function handleFilterPhotographerChange(photographer: Photographer) {
        setSelectedPhotographer(photographer);
    }

    if (isError) {
        handleError(error);
    }

    let totalNumberOfImages = data ? data.totalNumberOfImages : 0;
    let availablePhotographers = data ? data.availablePhotographers : [];
    let favoritedImages = data ? data.images : [];

    let numberOfPages = Math.ceil(totalNumberOfImages / NUMBER_OF_IMAGES_PER_PAGE);

    return (
        <FavoriteImagesPage
            {...props}
            availablePhotographers={
                availablePhotographers
                    ? availablePhotographers.map((photographer) => ({
                          name: photographer.name,
                          id: photographer.id
                      }))
                    : []
            }
            numberOfPages={numberOfPages}
            onChangePage={(event, value) => handlePageChange(event, value)}
            onChangePhotographerFilter={handleFilterPhotographerChange}
            currentPage={currentPage}
            list={
                favoritedImages
                    ? favoritedImages.map((image) => ({
                          id: image.id,
                          reference: image.reference,
                          photographerName: image.photographerName
                      }))
                    : []
            }
            previousSelectedPhotographer={data ? data.selectedPhotographer : null}
        />
    );
}

export default withRouter(DataLayer);
